import { useEffect, useState } from 'react';
import {
  createManagementKey,
  listManagementKeys,
  revokeManagementKey,
  type ManagementKey,
  type ManagementKeyCreated,
} from '../lib/api';

export default function ManagementKeys() {
  const [keys, setKeys] = useState<ManagementKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<ManagementKeyCreated | null>(null);

  useEffect(() => {
    reload();
  }, []);

  async function reload() {
    try {
      const response = await listManagementKeys();
      setKeys(response.keys);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load API keys.');
    }
  }

  return (
    <section className="block-brutal ">
      <header className="px-6 py-5 border-b-2 border-border flex items-baseline justify-between">
        <div>
          <h2 className="font-display text-xl uppercase">Management API keys</h2>
          <p className="text-sm text-muted-foreground mt-1">
            For scripting your own Key Receipt account. Separate from inbound
            tokens — use these to CRUD inbound tokens, pause/resume, and read
            usage via the <code className="font-mono">/kr/v1/*</code> REST API.
          </p>
        </div>
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-spray-magenta font-bold">
          // programmable
        </div>
      </header>

      {justCreated && (
        <NewKeyBanner created={justCreated} onDismiss={() => { setJustCreated(null); reload(); }} />
      )}

      <MintForm onCreated={(created) => setJustCreated(created)} onError={setError} />

      {error && (
        <div className="px-6 py-4 border-t border-border bg-muted text-sm text-foreground font-mono">
          {error}
        </div>
      )}

      <KeysTable keys={keys} onRevoked={reload} />
    </section>
  );
}

function MintForm({
  onCreated,
  onError,
}: {
  onCreated: (created: ManagementKeyCreated) => void;
  onError: (message: string | null) => void;
}) {
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!label.trim()) return;
    setSubmitting(true);
    onError(null);
    try {
      const created = await createManagementKey(label.trim());
      onCreated(created);
      setLabel('');
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Could not mint API key.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="px-6 py-5 border-b-2 border-border flex flex-col md:flex-row gap-3 items-stretch md:items-end">
      <label className="flex-1 flex flex-col gap-1.5">
        <span className="text-xs font-mono tracking-wide uppercase text-muted-foreground">Label</span>
        <input
          type="text"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="e.g. my laptop, ci/cd, onboarding script"
          className="border-2 border-border bg-card  px-4 py-2.5 text-sm focus:outline-none focus:border-foreground"
        />
      </label>
      <button
        type="submit"
        disabled={submitting || !label.trim()}
        className="bg-spray-yellow text-primary-foreground font-medium px-5 py-3  hover:bg-primary transition disabled:opacity-60"
      >
        {submitting ? 'Minting…' : 'Mint API key'}
      </button>
    </form>
  );
}

function NewKeyBanner({
  created,
  onDismiss,
}: {
  created: ManagementKeyCreated;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const curlExample = `curl https://api.keyreceipt.com/kr/v1/inbound \\
  -H "Authorization: Bearer ${created.token}"`;

  return (
    <div className="bg-spray-yellow text-primary-foreground border-b-2 border-foreground px-6 py-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-spray-magenta font-bold">
            // New API key · shown once
          </div>
          <div className="font-display text-2xl uppercase mt-2 leading-tight">{created.label}</div>

          <div className="mt-4 flex items-center gap-3">
            <code className="font-mono text-[13px] bg-background text-foreground border-2 border-foreground px-3 py-2.5 break-all flex-1">
              {created.token}
            </code>
            <button
              onClick={copy}
              className="text-xs font-bold uppercase tracking-wider bg-background text-foreground border-2 border-foreground px-4 py-2.5 hover:translate-y-[-1px] transition-transform flex-shrink-0"
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>

          <div className="mt-5 bg-background text-foreground border-2 border-foreground overflow-hidden">
            <div className="px-3 py-2 border-b-2 border-foreground bg-card">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-spray-yellow font-bold">
                // Test it
              </span>
            </div>
            <pre className="font-mono text-[11.5px] text-foreground p-3 overflow-x-auto whitespace-pre">
              {curlExample}
            </pre>
          </div>

          <div className="mt-4 text-[11px] uppercase tracking-wider font-bold text-primary-foreground/70">
            Anyone with this key can mint or revoke your inbound tokens. Treat
            it like a production credential.
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="text-[10px] uppercase tracking-wider font-bold text-primary-foreground/70 hover:text-primary-foreground transition flex-shrink-0"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function KeysTable({
  keys,
  onRevoked,
}: {
  keys: ManagementKey[] | null;
  onRevoked: () => void;
}) {
  if (keys === null) {
    return <div className="px-6 py-6 text-sm text-muted-foreground font-mono">Loading…</div>;
  }
  if (keys.length === 0) {
    return (
      <div className="px-6 py-10 text-center text-sm text-muted-foreground">
        No management API keys yet. Mint one above to start scripting your account.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <th className="px-6 py-3 font-normal">Label</th>
            <th className="px-6 py-3 font-normal">Token</th>
            <th className="px-6 py-3 font-normal">Last used</th>
            <th className="px-6 py-3 font-normal text-right">Status</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => (
            <KeyRow key={key.id} entry={key} onRevoked={onRevoked} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KeyRow({
  entry,
  onRevoked,
}: {
  entry: ManagementKey;
  onRevoked: () => void;
}) {
  const [revoking, setRevoking] = useState(false);
  const revoked = entry.revokedAt !== null;

  async function handleRevoke() {
    if (!confirm(`Revoke "${entry.label}"? Scripts using this key will start returning 401.`)) return;
    setRevoking(true);
    try {
      await revokeManagementKey(entry.id);
    } finally {
      setRevoking(false);
      onRevoked();
    }
  }

  return (
    <tr className={'border-t border-border ' + (revoked ? 'opacity-50' : '')}>
      <td className="px-6 py-4 text-foreground">{entry.label}</td>
      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{entry.tokenPreview}</td>
      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
        {entry.lastUsedAt ? new Date(entry.lastUsedAt).toLocaleDateString() : '—'}
      </td>
      <td className="px-6 py-4 text-right">
        {revoked ? (
          <span className="font-mono text-xs text-muted-foreground">
            Revoked {entry.revokedAt ? new Date(entry.revokedAt).toLocaleDateString() : ''}
          </span>
        ) : (
          <button
            onClick={handleRevoke}
            disabled={revoking}
            className="text-xs font-medium text-spray-yellow hover:text-foreground transition disabled:opacity-60"
          >
            {revoking ? 'Revoking…' : 'Revoke'}
          </button>
        )}
      </td>
    </tr>
  );
}
